import { List } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import FilterField from "../../components/filter/FilterField";
import FilterGroup from "../../components/filter/FilterGroup";
import FilterRow from "../../components/filter/FilterRow";
import FormButton from "../../components/form/FormButton";
import FormCheckbox from "../../components/form/FormCheckbox";
import SingleFormDatePicker from "../../components/form/formDatePicker/SingleFormDatePicker";
import FormDropdown from "../../components/form/FormDropdown";
import FormDurationPicker from "../../components/form/FormDurationPicker";
import FormList from "../../components/form/FormList";
import FormRadio from "../../components/form/FormRadio";
import FormTextField from "../../components/form/FormTextField";
import { FILTER_LABELS, matchSegmentOptions } from "../../constants/filter";
import narrativeFilterOptions from "../../constants/narrativeFilterOptions";
import { PropertyFormatIdentifier } from "../../constants/propertyFormats";
import { referenceSelectors } from "../../modules/definition/references";
import { immutableRecords, PropTypeShapes } from "../../types";
import { chooseValueForHarvard } from "../../utils/codeSplit";
import filterUtils from "../../utils/filter";
import preprocessorQuery from "../../utils/preprocessorQuery";
import AddFilterDropdown from "./AddFilterDropdown";
import CurrentFilterViewHeader from "./CurrentFilterViewHeader";

const isHarvard = preprocessorQuery.isHarvard();

const renderFilterGroup = (groupId, label, subLabel, children, isActive, expanded, onClear, useProps, exposeFilter) => {
  const performClear = e => {
    onClear();
    e.stopPropagation();
  };
  const performClearAndClose = e => {
    performClear(e);
    useProps.onGroupToggled(groupId, false);
  };

  return (
    <FilterGroup
      key={groupId}
      label={label}
      subLabel={subLabel}
      isActive={isActive}
      alwaysShowHeader={exposeFilter}
      onClear={performClear}
      onClearAndClose={performClearAndClose}
      isCollapsed={!expanded}
      onToggleCollapse={() => useProps.onGroupToggled(groupId, !expanded)}
    >
      {children}
    </FilterGroup>
  );
};

const renderDurationGroup = (groupId, filter = immutableRecords.RangeFilterPartRecord(), label, onChange, useProps) => {
  const expanded = useProps.expandedGroups.contains(groupId);
  const rows = expanded
    ? [
      <FilterRow key="durationMin" label="Min">
        <FilterField>
          <FormDurationPicker
            value={filter.above}
            onChange={value => onChange(useProps.filterId, value, filter.below)}
            calendar={useProps.calendar}
          />
        </FilterField>
      </FilterRow>,
      <FilterRow key="durationMax" label="Max">
        <FilterField>
          <FormDurationPicker
            value={filter.below}
            onChange={value => onChange(useProps.filterId, filter.above, value)}
            calendar={useProps.calendar}
          />
        </FilterField>
      </FilterRow>
    ]
    : null;
  const isChecked = Boolean(filter.above || filter.below);
  const subLabel = filterUtils.getDurationFilterSubLabel(filter);
  const exposeFilter = useProps.exposedFilterTypes.includes(groupId);
  return renderFilterGroup(
    groupId,
    label,
    subLabel,
    rows,
    isChecked,
    expanded,
    () => onChange(useProps.filterId, null, null),
    useProps,
    exposeFilter
  );
};

const getNarrativeRangeOptions = (useProps, isStart) => {
  return useProps.narrativeIds
    .map(id => {
      const displayItem = useProps.displayItemsById.get(id);
      const indent = displayItem.narrativePosition.count() - 1;
      const prefix = indent > 0 ? " ".repeat(indent * 5) : "";
      let label = prefix + displayItem.narrativeDisplayLabel;
      return {
        label,
        value: id
      };
    })
    .unshift({
      label: isStart ? "Start" : "End",
      value: null
    })
    .toArray();
};

const renderNarrativeGroup = (
  groupId,
  filter = immutableRecords.NarrativeFilterPartRecord(),
  label,
  onChange,
  useProps
) => {
  const expanded = useProps.expandedGroups.contains(groupId);

  const fromOptions = getNarrativeRangeOptions(useProps, true);
  const toOptions = getNarrativeRangeOptions(useProps, false);

  const rows = expanded
    ? [
      <FilterRow key="narrativeInclusion">
        <FilterField>
          <FormRadio
            label="In narrative"
            isChecked={filter.narrativeStatus === narrativeFilterOptions.IN_NARRATIVE}
            onChange={() => onChange(useProps.filterId, narrativeFilterOptions.IN_NARRATIVE, filter.from, filter.to)}
          />
        </FilterField>
        <FilterField>
          <FormRadio
            label="Not in narrative"
            isChecked={filter.narrativeStatus === narrativeFilterOptions.NOT_IN_NARRATIVE}
            onChange={() => onChange(useProps.filterId, narrativeFilterOptions.NOT_IN_NARRATIVE, null, null)}
          />
        </FilterField>
      </FilterRow>,
      <FilterRow key="narrativeFrom" label="From" indent={true}>
        <FilterField>
          <FormDropdown
            disabled={filter.narrativeStatus !== narrativeFilterOptions.IN_NARRATIVE}
            options={fromOptions}
            value={filter.from}
            allowSearch={true}
            preserveSpaces={true}
            onChange={value => onChange(useProps.filterId, filter.narrativeStatus, value, filter.to)}
          />
        </FilterField>
      </FilterRow>,
      <FilterRow key="narrativeTo" label="To" indent={true}>
        <FilterField>
          <FormDropdown
            disabled={filter.narrativeStatus !== narrativeFilterOptions.IN_NARRATIVE}
            options={toOptions}
            value={filter.to}
            allowSearch={true}
            preserveSpaces={true}
            onChange={value => onChange(useProps.filterId, filter.narrativeStatus, filter.from, value)}
          />
        </FilterField>
      </FilterRow>
    ]
    : null;

  const isChecked = Boolean(filter.narrativeStatus);
  let subLabel = null;
  if (filter.narrativeStatus === narrativeFilterOptions.IN_NARRATIVE) {
    const fromOpt = _.find(fromOptions, o => o.value === filter.from);
    const toOpt = _.find(toOptions, o => o.value === filter.to);
    subLabel = `${fromOpt ? fromOpt.label : "Start"} to ${toOpt ? toOpt.label : "End"}`;
  } else {
    subLabel = "Not in Narrative";
  }

  const exposeFilter = useProps.exposedFilterTypes.includes(groupId);
  return renderFilterGroup(
    groupId,
    label,
    subLabel,
    rows,
    isChecked,
    expanded,
    () => onChange(useProps.filterId, null, null),
    useProps,
    exposeFilter
  );
};

const renderDateGroup = (groupId, filter = immutableRecords.RangeFilterPartRecord(), label, onChange, useProps) => {
  const expanded = useProps.expandedGroups.contains(groupId);
  const calendar = useProps.calendar;
  const latest = filter.below;
  const earlist = filter.above;
  const subLabel = filterUtils.getDateFilterSubLabel(filter, calendar);
  const rows = expanded
    ? [
      <FilterRow key="earliest" label="Earliest">
        <FilterField>
          <SingleFormDatePicker
            value={earlist}
            calendar={calendar}
            onChange={value => onChange(useProps.filterId, value, latest)}
          />
        </FilterField>
      </FilterRow>,
      <FilterRow key="latest" label="Latest">
        <FilterField>
          <SingleFormDatePicker
            value={latest}
            calendar={calendar}
            onChange={value => onChange(useProps.filterId, earlist, value)}
          />
        </FilterField>
      </FilterRow>
    ]
    : null;
  const isChecked = Boolean(earlist || latest);
  const exposeFilter = useProps.exposedFilterTypes.includes(groupId);
  return renderFilterGroup(
    groupId,
    label,
    subLabel,
    rows,
    isChecked,
    expanded,
    () => onChange(useProps.filterId, null, null),
    useProps,
    exposeFilter
  );
};

const renderMatchSegment = (filter, onChange, useProps) => (
  <FilterRow key="match" className="currentFilterMatchCriteria">
    <FilterField flex={1}>
      <FormDropdown
        options={matchSegmentOptions}
        value={filter.match}
        onChange={value => onChange(useProps.filterId, filter.terms.toArray(), value)}
        className="filterMatchDropdown"
      />
    </FilterField>
  </FilterRow>
);

const renderStringGroup = (groupId, filter = immutableRecords.ListFilterPartRecord(), label, onChange, useProps) => {
  const str = filter.terms.toArray().join(", ");
  const expanded = useProps.expandedGroups.contains(groupId);
  const isChecked = str.length > 0;
  const row = expanded
    ? [
      renderMatchSegment(filter, onChange, useProps),
      <FilterRow key="terms">
        <FilterField>
          <FormTextField
            placeholder="Type filter terms, separated by comma"
            value={str}
            onChange={value =>
              onChange(
                useProps.filterId,
                value
                  .split(",")
                  .map(t => _.trim(t))
                  .filter(t => t),
                filter.match
              )
            }
          />
        </FilterField>
      </FilterRow>
    ]
    : null;

  const subLabel = filterUtils.getStringFilterSubLabel(filter);

  const exposeFilter = useProps.exposedFilterTypes.includes(groupId);

  return renderFilterGroup(
    groupId,
    label,
    subLabel,
    row,
    isChecked,
    expanded,
    () => onChange(useProps.filterId, []),
    useProps,
    exposeFilter
  );
};

const renderBoolGroup = (groupId, filter = immutableRecords.BoolFilterPartRecord(), label, onChange, useProps) => {
  const expanded = useProps.expandedGroups.contains(groupId);
  const isGroupChecked = filter.expected === true || filter.expected === false;
  const isValueChecked = Boolean(filter.expected);
  const rows = expanded
    ? [
      <FilterRow key="yes">
        <FilterField flex={1}>
          <FormRadio label="Yes" isChecked={isValueChecked} onChange={() => onChange(useProps.filterId, true)} />
        </FilterField>
        <FilterField flex={1}>
          <FormRadio
            label="No"
            isChecked={isGroupChecked && !isValueChecked}
            onChange={() => onChange(useProps.filterId, false)}
          />
        </FilterField>
        <FilterField flex={2} />
      </FilterRow>
    ]
    : null;

  const subLabel = filterUtils.getBooleanFilterSubLabel(filter);

  const exposeFilter = useProps.exposedFilterTypes.includes(groupId);

  return renderFilterGroup(
    groupId,
    label,
    subLabel,
    rows,
    isGroupChecked || exposeFilter,
    expanded,
    () => onChange(useProps.filterId, null),
    useProps,
    exposeFilter
  );
};

const renderNumericGroup = (groupId, filter = immutableRecords.RangeFilterPartRecord(), label, onChange, useProps) => {
  const expanded = useProps.expandedGroups.contains(groupId);
  const isChecked = !_.isNil(filter.above) || !_.isNil(filter.below);
  const rows = expanded
    ? [
      <FilterRow key="above" label="Above">
        <FilterField>
          <FormTextField value={filter.above} onChange={value => onChange(useProps.filterId, value, filter.below)} />
        </FilterField>
      </FilterRow>,
      <FilterRow key="below" label="Below">
        <FilterField>
          <FormTextField value={filter.below} onChange={value => onChange(useProps.filterId, filter.above, value)} />
        </FilterField>
      </FilterRow>
    ]
    : null;

  const subLabel = filterUtils.getNumericFilterSubLabel(filter);

  const exposeFilter = useProps.exposedFilterTypes.includes(groupId);

  return renderFilterGroup(
    groupId,
    label,
    subLabel,
    rows,
    isChecked,
    expanded,
    () => onChange(useProps.filterId, null, null),
    useProps,
    exposeFilter
  );
};

const renderCheckboxListGroup = (groupId, terms, options, onChange) => {
  const rows = options
    .map(opt => (
      <FilterRow key={opt.id}>
        <FilterField>
          <FormCheckbox
            label={opt.label}
            checkboxState={terms.contains(opt.id)}
            onChange={value =>
              onChange(value ? terms.push(opt.id).toArray() : terms.filter(t => t !== opt.id).toArray())
            }
          />
        </FilterField>
      </FilterRow>
    ))
    .toArray();
  return rows;
};

const renderPopoverListGroup = (groupId, terms, options, onChange) => {
  const values = terms
    .map(t => {
      const opt = options.find(o => o.id === t);
      return {
        label: opt ? opt.label : "",
        value: t
      };
    })
    .toArray();

  const listOptions = options
    .map(o => {
      return {
        label: o.label,
        value: o.id,
        displayId: o.displayId
      };
    })
    .toArray();

  const listId = `formList-${groupId}`;

  return (
    <FilterRow key={groupId}>
      <FilterField>
        <FormList
          id={listId}
          values={values}
          options={listOptions}
          emptyLabel={"Click to add first item"}
          canDelete={true}
          canAdd={true}
          allowSearch={true}
          onAddItem={optionId => onChange(terms.push(optionId).toArray())}
          onDeleteItem={optionId => onChange(terms.filter(t => t !== optionId).toArray())}
        />
      </FilterField>
    </FilterRow>
  );
};

const renderListGroup = (
  groupId,
  filter = immutableRecords.ListFilterPartRecord(),
  label,
  options,
  showMatch,
  onChange,
  useProps
) => {
  const expanded = useProps.expandedGroups.contains(groupId);
  const isChecked = filter.terms.count() > 0;
  const viewOptions = options.map(o => (!o.label ? o.set("label", "Untitled") : o));
  const list = expanded
    ? viewOptions.count() > 10
      ? renderPopoverListGroup(groupId, filter.terms, viewOptions, terms =>
        onChange(useProps.filterId, terms, filter.match)
      )
      : renderCheckboxListGroup(groupId, filter.terms, viewOptions, terms =>
        onChange(useProps.filterId, terms, filter.match)
      )
    : null;

  const matchSegment = showMatch ? [renderMatchSegment(filter, onChange, useProps)] : [];

  const children = _.concat(matchSegment, list);

  const subLabel = filterUtils.getListFilterSubLabel(filter, viewOptions, true);

  const exposeFilter = useProps.exposedFilterTypes.includes(groupId);

  return renderFilterGroup(
    groupId,
    label,
    subLabel,
    children,
    isChecked,
    expanded,
    () => onChange(useProps.filterId, []),
    useProps,
    exposeFilter
  );
};

const renderTypeReferenceGroup = (type, useProps) => {
  const isReferenceTarget = useProps.references.filter(reference => reference.types.includes(type.id)).count() > 0;
  const itemsOfTypeExist = useProps.itemIdsByType.get(type.id).count() > 0;
  return isReferenceTarget && itemsOfTypeExist
    ? renderListGroup(
      type.id,
      useProps.filter.relationshipsByType.get(type.id),
      type.label,
      useProps.itemIdsByType.get(type.id).map(id => useProps.displayItemsById.get(id)),
      true,
      (filterId, terms, match) => useProps.onTypeReferenceFilterChanged(filterId, type.id, terms, match),
      useProps
    )
    : null;
};

const renderReferenceGroup = (reference, useProps) => {
  const referenceHasTargetItems =
    reference.types.filter(type => useProps.itemIdsByType.get(type).count() > 0).count() > 0;
  return referenceHasTargetItems
    ? renderListGroup(
      reference.id,
      useProps.filter.relationshipsByReference.get(reference.id),
      reference.label,
      itemIdsForReference(reference, useProps.itemIdsByType).map(id => useProps.displayItemsById.get(id)),
      true,
      (filterId, terms, match) => useProps.onReferenceFilterChanged(filterId, reference.id, terms, match),
      useProps
    )
    : null;
};

const renderPropertyGroup = (p, useProps) => {
  switch (p.format) {
    case PropertyFormatIdentifier.text:
    case PropertyFormatIdentifier.multitext:
      return renderStringGroup(
        p.id,
        useProps.filter.textProperties.get(p.id),
        p.label,
        (filterId, terms, match) => useProps.onTextPropertyChanged(filterId, p.id, terms, match),
        useProps
      );
    case PropertyFormatIdentifier.list:
      return renderListGroup(
        p.id,
        useProps.filter.textProperties.get(p.id),
        p.label,
        p.allowed,
        p.multiple,
        (filterId, terms, match) => useProps.onTextPropertyChanged(filterId, p.id, terms, match),
        useProps
      );
    case PropertyFormatIdentifier.boolean:
      return renderBoolGroup(
        p.id,
        useProps.filter.boolProperties.get(p.id),
        p.label,
        (filterId, expected) => useProps.onBoolPropertyChanged(filterId, p.id, expected),
        useProps
      );
    case PropertyFormatIdentifier.integer:
    case PropertyFormatIdentifier.double:
    case PropertyFormatIdentifier.percentage:
    case PropertyFormatIdentifier.currency:
      return renderNumericGroup(
        p.id,
        useProps.filter.numericProperties.get(p.id),
        p.label,
        (filterId, above, below) => useProps.onNumericPropertyChanged(filterId, p.id, above, below),
        useProps
      );
    default:
      break;
  }
};

const itemIdsForReference = (ref, itemIdsByType) => ref.types.map(typeId => itemIdsByType.get(typeId)).flatten();

const addFilterDropdown = useProps => (
  <AddFilterDropdown
    collapsedGroups={useProps.collapsedGroups}
    onGroupToggled={useProps.onGroupToggled}
    title="More Filters"
    backText="Filters"
    filterId={useProps.filterId}
    addButtonLabel={useProps.addButtonLabel}
  />
);

const getRowHeader = chooseValueForHarvard(useProps => {
  const { rowViewId, rowOnRemove, rowIndex, openViewIds, activeViewId } = useProps;
  const selected = rowViewId === activeViewId;
  const multiple = openViewIds.size > 1;
  if (rowIndex === 0) {
    return (
      <div className={`filterGroupRowHeader ${selected ? "selected" : ""} ${multiple ? "multiple" : ""}`}>
        <span>{openViewIds.size === 1 ? `` : `Top View`}</span>
      </div>
    );
  } else {
    return (
      <div className={`filterGroupRowHeader ${selected ? "selected" : ""} ${multiple ? "multiple" : ""}`}>
        <span>{`${rowIndex === openViewIds.size - 1 ? "Bottom" : "Middle"} View`}</span>
        <FormButton label="Close" linkStyle={true} onClick={() => rowOnRemove(rowViewId)} />
      </div>
    );
  }
}, null);

const getTagsOptions = tagsMap => {
  return tagsMap.reduce((options, tag, id) => {
    return options.push({ label: tag, id });
  }, List());
};

const CurrentFilterView = props => {
  const onChangeFilterMatchCriteria = newValue => props.onChangeFilterMatchCriteria(props.filterId, newValue);

  return (
    <div className="currentFilterView">
      {props.hideHeader ? null : (
        <CurrentFilterViewHeader
          onFilterClearClick={props.onFilterClearClick}
          onSaveFilter={props.onSaveFilter}
          filterId={props.filterId}
          filterIsActive={props.filterIsActive}
          title="Save Filter"
          backText="Filters"
          filterMatchCriteria={props.filter.matchCriteria}
          onChangeFilterMatchCriteria={onChangeFilterMatchCriteria}
          savedFilters={props.savedFilters}
        />
      )}
      {props.showRowHeader ? getRowHeader(props) : null}
      <div className="filterWrapper">
        {renderListGroup(
          "color",
          props.filter.color,
          FILTER_LABELS.color,
          props.colors,
          false,
          props.onColorFilterChange,
          props
        )}
        {renderListGroup(
          "type",
          props.filter.type,
          FILTER_LABELS.type,
          props.types,
          false,
          props.onTypeFilterChange,
          props
        )}
        {props.tags.size > 0
          ? renderListGroup(
            "tags",
            props.filter.tags,
            FILTER_LABELS.tags,
            getTagsOptions(props.tags),
            true,
            props.onTagsFilterChange,
            props
          )
          : null}
        {renderDateGroup("date", props.filter.date, FILTER_LABELS.date, props.onDateFilterChange, props)}
        {renderDurationGroup(
          "duration",
          props.filter.duration,
          FILTER_LABELS.duration,
          props.onDurationFilterChange,
          props
        )}
        {renderStringGroup("label", props.filter.label, FILTER_LABELS.label, props.onLabelFilterChange, props)}
        {renderStringGroup("summary", props.filter.summary, FILTER_LABELS.summary, props.onSummaryFilterChange, props)}
        {renderNarrativeGroup(
          "narrative",
          props.filter.narrative,
          FILTER_LABELS.narrative,
          props.onNarrativeFilterChange,
          props
        )}
        {props.types.map(type => renderTypeReferenceGroup(type, props))}
        {props.references
          .filter(
            r =>
              r.types.count() > 1 ||
              referenceSelectors.getReferencesWithTypeFromReferences(props.references, r.types.get(0)).count() > 1
          )
          .map(reference => renderReferenceGroup(reference, props))}
        {props.properties.map(p => renderPropertyGroup(p, props))}
        {!isHarvard || !props.disableFilteringMore ? addFilterDropdown(props) : null}
      </div>
    </div>
  );
};

CurrentFilterView.propTypes = {
  activeViewId: PropTypes.string,
  onFilterClearClick: PropTypes.func.isRequired,
  onSaveFilter: PropTypes.func.isRequired,
  onDeleteFilter: PropTypes.func.isRequired,
  onChangeFilterMatchCriteria: PropTypes.func.isRequired,
  onColorFilterChange: PropTypes.func.isRequired,
  onLabelFilterChange: PropTypes.func.isRequired,
  onSummaryFilterChange: PropTypes.func.isRequired,
  onDateFilterChange: PropTypes.func.isRequired,
  onDurationFilterChange: PropTypes.func.isRequired,
  onNarrativeFilterChange: PropTypes.func.isRequired,
  onTagsFilterChange: PropTypes.func.isRequired,
  onTypeFilterChange: PropTypes.func.isRequired,
  onTypeReferenceFilterChanged: PropTypes.func.isRequired,
  onReferenceFilterChanged: PropTypes.func.isRequired,
  onTextPropertyChanged: PropTypes.func.isRequired,
  onNumericPropertyChanged: PropTypes.func.isRequired,
  onBoolPropertyChanged: PropTypes.func.isRequired,
  onGroupToggled: PropTypes.func.isRequired,

  savedFilters: ImmutablePropTypes.map.isRequired,
  onLoadFilter: PropTypes.func.isRequired,

  filterMode: PropTypes.string.isRequired,
  filterId: PropTypes.string.isRequired,
  filterIsActive: PropTypes.bool.isRequired,
  countActiveFilterGroups: PropTypes.number.isRequired,
  colors: ImmutablePropTypes.listOf(PropTypeShapes.colorDefinition).isRequired,
  types: ImmutablePropTypes.listOf(PropTypeShapes.typeDefinition).isRequired,
  tags: ImmutablePropTypes.map.isRequired,
  references: ImmutablePropTypes.listOf(PropTypeShapes.referenceDefinition).isRequired,
  properties: ImmutablePropTypes.listOf(PropTypeShapes.propertyDefinition).isRequired,
  expandedGroups: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  collapsedGroups: ImmutablePropTypes.list.isRequired,
  itemIdsByType: ImmutablePropTypes.map.isRequired,
  displayItemsById: ImmutablePropTypes.map.isRequired,
  narrativeIds: ImmutablePropTypes.list.isRequired,
  filter: PropTypeShapes.filter.isRequired,
  calendar: PropTypeShapes.calendar.isRequired,

  hideHeader: PropTypes.bool,
  showRowHeader: PropTypes.bool,

  //#!if harvard === true
  // For Harvard where multiple filter views are shown as rows
  rowViewId: PropTypes.string,
  rowOnRemove: PropTypes.func,
  rowIndex: PropTypes.number,
  openViewIds: ImmutablePropTypes.listOf(PropTypes.string),
  disableSplitView: PropTypes.bool,
  disableFilteringMore: PropTypes.bool
  //#!endif
};

export default CurrentFilterView;
